import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { commissionsService } from '../../../services/commissionsService';

type CommissionStatus = 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'DELIVERED' | 'COMPLETED' | 'CANCELLED' | 'DISPUTED';

interface Commission {
  id: string;
  patronId: string;
  patronName?: string;
  patronEmail?: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: CommissionStatus;
  deadline?: string;
  estimatedDelivery?: string;
  createdAt: string;
  acceptedAt?: string;
  startedAt?: string;
  deliveredAt?: string;
  completedAt?: string;
  deliveryNote?: string;
  deliverables?: Array<{
    id: string;
    fileName: string;
    fileUrl: string;
    uploadedAt: string;
  }>;
}

const CommissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [commission, setCommission] = useState<Commission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    fetchCommission();
  }, [id]);

  const fetchCommission = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await commissionsService.get(id);
      setCommission(data.commission);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load commission');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await commissionsService.accept(id, estimatedDelivery || undefined);
      await fetchCommission();
      setShowAcceptModal(false);
      setEstimatedDelivery('');
    } catch (err: any) {
      alert(err.message || 'Failed to accept commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectReason.trim()) return;
    try {
      setActionLoading(true);
      await commissionsService.reject(id, rejectReason);
      await fetchCommission();
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err: any) {
      alert(err.message || 'Failed to reject commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await commissionsService.start(id);
      await fetchCommission();
    } catch (err: any) {
      alert(err.message || 'Failed to start commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadDeliverable = async () => {
    if (!id || !selectedFile) return;
    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      await commissionsService.uploadDeliverable(id, formData);
      await fetchCommission();
      setSelectedFile(null);
    } catch (err: any) {
      alert(err.message || 'Failed to upload file');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await commissionsService.complete(id, deliveryNote || undefined);
      await fetchCommission();
      setShowCompleteModal(false);
      setDeliveryNote('');
    } catch (err: any) {
      alert(err.message || 'Failed to complete commission');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: CommissionStatus) => {
    const colors: Record<CommissionStatus, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      ACCEPTED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-purple-100 text-purple-800',
      DELIVERED: 'bg-indigo-100 text-indigo-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      DISPUTED: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${colors[status]}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !commission) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Commission not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/commissions')}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          ← Back to Commissions
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{commission.title}</h1>
            <p className="text-gray-600 mt-1">Commission Request</p>
          </div>
          {getStatusBadge(commission.status)}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{commission.description}</p>
          </div>

          {/* Deliverables */}
          {(commission.status === 'IN_PROGRESS' ||
            commission.status === 'DELIVERED' ||
            commission.status === 'COMPLETED') && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Deliverables</h2>
              
              {commission.status === 'IN_PROGRESS' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload File
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      onClick={handleUploadDeliverable}
                      disabled={!selectedFile || actionLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}

              {commission.deliverables && commission.deliverables.length > 0 ? (
                <div className="space-y-2">
                  {commission.deliverables.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{file.fileName}</p>
                        <p className="text-sm text-gray-500">
                          Uploaded {formatDate(file.uploadedAt)}
                        </p>
                      </div>
                      <a
                        href={file.fileUrl}
                        download
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No files uploaded yet</p>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                <div>
                  <p className="font-medium text-gray-900">Request Submitted</p>
                  <p className="text-sm text-gray-500">{formatDate(commission.createdAt)}</p>
                </div>
              </div>
              {commission.acceptedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Accepted</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.acceptedAt)}</p>
                  </div>
                </div>
              )}
              {commission.startedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-purple-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Started Work</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.startedAt)}</p>
                  </div>
                </div>
              )}
              {commission.deliveredAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-indigo-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Delivered</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.deliveredAt)}</p>
                  </div>
                </div>
              )}
              {commission.completedAt && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-2 bg-green-600 rounded-full"></div>
                  <div>
                    <p className="font-medium text-gray-900">Completed</p>
                    <p className="text-sm text-gray-500">{formatDate(commission.completedAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Info & Actions */}
        <div className="space-y-6">
          {/* Amount */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Amount</h3>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(commission.amount, commission.currency)}
            </p>
            {commission.status === 'ACCEPTED' && (
              <p className="text-sm text-blue-600 mt-2">Escrowed - will be released on completion</p>
            )}
          </div>

          {/* Patron Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Patron</h3>
            <p className="font-medium text-gray-900">{commission.patronName || 'Anonymous'}</p>
            {commission.patronEmail && (
              <p className="text-sm text-gray-600 mt-1">{commission.patronEmail}</p>
            )}
          </div>

          {/* Deadline */}
          {commission.deadline && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Requested Deadline</h3>
              <p className="text-gray-900">{formatDate(commission.deadline)}</p>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Actions</h3>
            <div className="space-y-2">
              {commission.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => setShowAcceptModal(true)}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Accept Commission
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Decline
                  </button>
                </>
              )}
              {commission.status === 'ACCEPTED' && (
                <button
                  onClick={handleStart}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  Start Work
                </button>
              )}
              {commission.status === 'IN_PROGRESS' && (
                <button
                  onClick={() => setShowCompleteModal(true)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Mark as Complete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Accept Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Accept Commission</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated Delivery Date (Optional)
            </label>
            <input
              type="date"
              value={estimatedDelivery}
              onChange={(e) => setEstimatedDelivery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={handleAccept}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Accept
              </button>
              <button
                onClick={() => setShowAcceptModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Decline Commission</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for declining
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              placeholder="Please explain why you're declining this commission..."
            />
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Decline
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Complete Commission</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Note (Optional)
            </label>
            <textarea
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              placeholder="Add any final notes for the patron..."
            />
            <div className="flex gap-2">
              <button
                onClick={handleComplete}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Complete
              </button>
              <button
                onClick={() => setShowCompleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionDetailPage;
